/**
 * Words and month arithmetic for a quote's PRICE picture (visits a month, what most months cost).
 *
 * The DATES on a quote are not worked out here any more. This file used to add "the next route weekday, plus a
 * week for biweekly" to the day agreed (nextRecurringDate) and to say "every Wednesday" from the weekday alone
 * (cadenceWords). That arithmetic knows nothing about which week a route is out — every weekday is a Week A and
 * a Week B route since Oct 5 2026 — so the customer's page named dates the app never booked (the audit of
 * Oct 6 2026, findings #14 and #15). Owner, Oct 5 2026: "make sure on site routes work eg start days and new
 * route days based on new route days". The first mow, the one after it and "then every other Monday, from …"
 * now come from the server (services/autoQuote.startPlansFor / bookingFor) and are only printed.
 */
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function parse(iso: string) { return new Date(String(iso).slice(0, 10) + 'T12:00:00'); }

export function longDay(iso: string) {
  return parse(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/** How many visits a month really has. Weekly 4, sometimes 5; biweekly 2, sometimes 3. */
export const MONTH_VISITS: Record<string, { usual: number; some: number }> = {
  weekly: { usual: 4, some: 5 },
  biweekly: { usual: 2, some: 3 },
};

export type MonthPicture = {
  visit: number;
  month: number;
  usualVisits: number | null;
  someVisits: number | null;
  mostMonths: number;
  someMonths: number | null;
};

/**
 * Every number a customer should see on a bid: a visit, a month, what most
 * months cost, and what some months cost. Nothing is derived then hidden.
 */
export function monthPicture(
  frequency: string | null | undefined,
  perVisit: number,
  monthly = 0,
): MonthPicture | null {
  const visit = Number(perVisit) || 0;
  const month = Number(monthly) || 0;
  if (!(visit > 0) && !(month > 0)) return null;
  const v = frequency ? MONTH_VISITS[frequency] : null;
  if (!v || !(visit > 0)) {
    return { visit, month, usualVisits: null, someVisits: null, mostMonths: month || visit, someMonths: null };
  }
  return {
    visit,
    month,
    usualVisits: v.usual,
    someVisits: v.some,
    mostMonths: visit * v.usual + month,
    someMonths: visit * v.some + month,
  };
}

/** What a month really costs at a per-visit price, plus any monthly program. */
export function monthWords(
  frequency: string | null | undefined,
  perVisit: number,
  fmt: (n: number) => string,
  monthly = 0,
) {
  const pic = monthPicture(frequency, perVisit, monthly);
  if (!pic || pic.usualVisits == null || pic.someVisits == null || pic.someMonths == null) return '';
  if (pic.month > 0) {
    return `Most months that is ${pic.usualVisits} visits plus ${fmt(pic.month)} a month, ${fmt(pic.mostMonths)}. Some months have ${pic.someVisits} visits and that month is ${fmt(pic.someMonths)}.`;
  }
  return `Most months that is ${pic.usualVisits} visits (${fmt(pic.mostMonths)}). Some months have ${pic.someVisits} (${fmt(pic.someMonths)}).`;
}
